import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { useOfferedCurrencies } from '../../api/currencies.api.js';
import type { IntegrationField } from '../../api/integrations.api';

/**
 * A setting that has one value per currency.
 *
 * A partner opens an account in a currency, and the account it hangs off is a
 * fact about that currency: GuruPay issues a EUR vIBAN under the EUR master and
 * a GBP one under the GBP master. Storing a single value would mean a GBP
 * account issued under the EUR master — a mistake nobody notices until money
 * arrives in the wrong place.
 *
 * A shared account is the same shape for the same reason: a EUR customer cannot
 * be paid into a GBP account.
 *
 * Not every currency has to be filled in. One is enough to be usable, and
 * opening an account in a currency with no entry is refused by name rather than
 * blocking the whole partner until every currency is answered for.
 *
 * The currencies to add come from Settings → Organisation → Currencies offered
 * (DEV-162); an entry for a currency no longer offered stays shown.
 */

/** One row of the map: either a bare value, or an IBAN and BIC pair. */
export type CurrencyMapValue = Record<string, string | { iban?: string; bic?: string }>;

/** An account the partner told us about, offered instead of typing. */
export interface DiscoveredAccount {
  uuid: string;
  name: string;
  iban: string;
  bic: string;
  currency: string;
}

interface CurrencyMapFieldProps {
  field: IntegrationField;
  /** The stored map, or whatever the operator has edited into it. */
  value: CurrencyMapValue;
  onChange: (next: CurrencyMapValue) => void;
  /** True when each currency needs an IBAN and a BIC rather than one value. */
  pair?: boolean;
  /**
   * What the partner said it has, if it has been asked.
   *
   * Offered per row, because the choice is per currency: which account is the
   * EUR one is a different question from which is the GBP one. Loading the
   * accounts and then leaving an operator to copy an IBAN across by hand is
   * how a digit goes missing.
   */
  discovered?: readonly DiscoveredAccount[];
  /** Shown, not edited (DEV-140: a role that may read the section but not change it). */
  readOnly?: boolean;
}

export function CurrencyMapField({
  field,
  value,
  onChange,
  pair = false,
  discovered = [],
  readOnly = false,
}: CurrencyMapFieldProps): JSX.Element {
  const [adding, setAdding] = useState('');
  const entries = Object.entries(value);
  const offered = useOfferedCurrencies();
  const unused = readOnly ? [] : offered.currencies.filter((c) => !(c in value));

  const setEntry = (currency: string, next: string | { iban?: string; bic?: string }): void => {
    onChange({ ...value, [currency]: next });
  };

  const remove = (currency: string): void => {
    const next = { ...value };
    delete next[currency];
    onChange(next);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2">
          {field.label}
          {field.required ? '' : ' (optional)'}
        </Typography>
        {entries.length === 0 ? (
          <Tooltip
            title={
              field.required
                ? 'Nothing is configured. Accounts cannot be opened at this partner until at least one currency is.'
                : 'Nothing is configured.'
            }
          >
            <Chip
              size="small"
              color={field.required ? 'warning' : 'default'}
              variant="outlined"
              label="Not set"
            />
          </Tooltip>
        ) : (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={`${entries.length} ${entries.length === 1 ? 'currency' : 'currencies'}`}
          />
        )}
      </Stack>

      <Stack spacing={1.5}>
        {entries.map(([currency, entry]) => (
          /* One line tall, so centred — not pinned to the top and nudged back
             down by a different magic margin per control, which is how the
             chip ended up 4px below the delete button it sits beside. Fixed
             widths on everything except the IBAN, so the columns line up
             down the rows rather than each row solving its own layout. */
          <Stack key={currency} direction="row" spacing={1} alignItems="center">
            <Chip label={currency} sx={{ minWidth: 64, flexShrink: 0 }} />
            {pair ? (
              <>
                <TextField
                  size="small"
                  sx={{ flex: 1, minWidth: 0 }}
                  label="IBAN"
                  placeholder={field.placeholder ?? ''}
                  value={typeof entry === 'string' ? entry : (entry.iban ?? '')}
                  onChange={(event) =>
                    setEntry(currency, {
                      ...(typeof entry === 'string' ? {} : entry),
                      iban: event.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly }}
                />
                <TextField
                  size="small"
                  sx={{ width: 180, flexShrink: 0 }}
                  label="BIC"
                  value={typeof entry === 'string' ? '' : (entry.bic ?? '')}
                  onChange={(event) =>
                    setEntry(currency, {
                      ...(typeof entry === 'string' ? { iban: entry } : entry),
                      bic: event.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly }}
                />
              </>
            ) : (
              <TextField
                size="small"
                sx={{ flex: 1, minWidth: 0 }}
                value={typeof entry === 'string' ? entry : (entry.iban ?? '')}
                onChange={(event) => setEntry(currency, event.target.value)}
                placeholder={field.placeholder ?? ''}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly }}
              />
            )}
            {discovered.length > 0 && !readOnly ? (
              <TextField
                select
                size="small"
                sx={{ width: 200, flexShrink: 0 }}
                label="Use a listed account"
                value=""
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => {
                  const chosen = discovered.find((a) => a.uuid === event.target.value);
                  if (chosen === undefined) return;
                  setEntry(
                    currency,
                    pair ? { iban: chosen.iban, bic: chosen.bic } : chosen.uuid,
                  );
                }}
              >
                <option value="">Choose…</option>
                {discovered
                  // An account the partner attributed to another currency is
                  // not an option for this one; one it said nothing about is.
                  .filter((a) => a.currency === '' || a.currency === currency)
                  .map((a) => (
                    <option key={`${a.uuid}-${a.currency}`} value={a.uuid}>
                      {a.name || a.iban || a.uuid}
                    </option>
                  ))}
              </TextField>
            ) : null}
            {readOnly ? null : (
              <Tooltip title={`Remove ${currency}`}>
                <IconButton size="small" sx={{ flexShrink: 0 }} onClick={() => remove(currency)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ))}
      </Stack>

      {unused.length > 0 ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: entries.length > 0 ? 2 : 0 }}>
          <TextField
            select
            size="small"
            label="Add a currency"
            value={adding}
            onChange={(event) => setAdding(event.target.value)}
            sx={{ minWidth: 160 }}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
          >
            <option value="" />
            {unused.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </TextField>
          <Button
            size="small"
            variant="outlined"
            disabled={adding === ''}
            onClick={() => {
              setEntry(adding, pair ? { iban: '', bic: '' } : '');
              setAdding('');
            }}
          >
            Add
          </Button>
        </Stack>
      ) : null}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {field.help}
      </Typography>
    </Box>
  );
}
